import React, { useState } from 'react';
import ModalPortal from './ModalPortal';
import DataTableCrudTest from './DataTableCrudTest';
import DataTreeCrudTest from './DataTreeCrudTest';
import CardGridTest from './CardGridTest';
import SidebarDocumentTest from './SidebarDocumentTest';
import LayoutVariationsDemo from './LayoutVariationsDemo';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  status: 'Active' | 'Inactive';
  created: string;
}

interface TreeNode {
  id: string;
  name: string;
  type: 'organization' | 'department' | 'team' | 'process' | 'document';
  status: 'Active' | 'Inactive' | 'Draft' | 'Review';
  children?: TreeNode[];
  metadata?: {
    owner?: string;
    created?: string;
    count?: number;
    description?: string;
  };
}

interface DataTableDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUser?: User | null;
  selectedTreeNode?: TreeNode | null;
}

const DataTableDetailModal: React.FC<DataTableDetailModalProps> = ({
  isOpen,
  onClose,
  selectedUser,
  selectedTreeNode
}) => {
  const [activeTab, setActiveTab] = useState('tab1');

  if (!isOpen || (!selectedUser && !selectedTreeNode)) return null;

  const selectedItem = selectedUser || selectedTreeNode;
  const itemTitle = selectedUser ? `Row ${selectedUser.id}` : `Node ${selectedTreeNode?.name}`;

  const tabTitles = {
    tab1: '📊 Data Table CRUD Pattern',
    tab2: '🌲 Hierarchical Tree CRUD Pattern',
    tab3: '🎯 Card Grid Layout Pattern',
    tab4: '📄 Sidebar + Document Pattern',
    tab5: '🏗️ Layout System Variations Demo'
  };

  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} size="detail">
      <div className="modal-header">
        <h2 className="text-title">Detail View - {itemTitle}</h2>
        <button
          className="modal-close-button"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

          <div className="modal-body">
            {selectedUser ? (
              /* Reused Tab System from LayoutTestPage for User data */
              <div className="module-content-area" style={{ height: '100%', border: 'none' }}>
                {/* Tabs Row */}
                <div className="tabs-row">
                  {Object.entries(tabTitles).map(([tabKey, title]) => (
                    <button
                      key={tabKey}
                      className={`interactive-tab-button ${activeTab === tabKey ? 'active' : ''}`}
                      onClick={() => setActiveTab(tabKey)}
                    >
                      {title}
                    </button>
                  ))}
                </div>

                {/* Tab Content Area */}
                <div className="tab-content-area">
                  {activeTab === 'tab1' && <DataTableCrudTest />}
                  {activeTab === 'tab2' && <DataTreeCrudTest />}
                  {activeTab === 'tab3' && <CardGridTest />}
                  {activeTab === 'tab4' && <SidebarDocumentTest />}
                  {activeTab === 'tab5' && <LayoutVariationsDemo />}
                </div>
              </div>
            ) : selectedTreeNode ? (
              /* Simple detail view for Tree Node */
              <div className="tree-node-details" style={{ padding: '20px' }}>
                <div className="detail-row" style={{ display: 'flex', marginBottom: '12px', alignItems: 'center' }}>
                  <label style={{ fontWeight: 'bold', width: '120px', color: '#374151' }}>ID:</label>
                  <span style={{ color: '#6b7280' }}>{selectedTreeNode.id}</span>
                </div>
                <div className="detail-row" style={{ display: 'flex', marginBottom: '12px', alignItems: 'center' }}>
                  <label style={{ fontWeight: 'bold', width: '120px', color: '#374151' }}>Name:</label>
                  <span style={{ color: '#6b7280' }}>{selectedTreeNode.name}</span>
                </div>
                <div className="detail-row" style={{ display: 'flex', marginBottom: '12px', alignItems: 'center' }}>
                  <label style={{ fontWeight: 'bold', width: '120px', color: '#374151' }}>Type:</label>
                  <span style={{ color: '#6b7280', textTransform: 'capitalize' }}>{selectedTreeNode.type}</span>
                </div>
                <div className="detail-row" style={{ display: 'flex', marginBottom: '12px', alignItems: 'center' }}>
                  <label style={{ fontWeight: 'bold', width: '120px', color: '#374151' }}>Status:</label>
                  <span style={{ color: '#6b7280' }}>{selectedTreeNode.status}</span>
                </div>
                <div className="detail-row" style={{ display: 'flex', marginBottom: '12px', alignItems: 'center' }}>
                  <label style={{ fontWeight: 'bold', width: '120px', color: '#374151' }}>Children:</label>
                  <span style={{ color: '#6b7280' }}>{selectedTreeNode.children?.length || 0} items</span>
                </div>
                {selectedTreeNode.metadata?.owner && (
                  <div className="detail-row" style={{ display: 'flex', marginBottom: '12px', alignItems: 'center' }}>
                    <label style={{ fontWeight: 'bold', width: '120px', color: '#374151' }}>Owner:</label>
                    <span style={{ color: '#6b7280' }}>{selectedTreeNode.metadata.owner}</span>
                  </div>
                )}
                {selectedTreeNode.metadata?.created && (
                  <div className="detail-row" style={{ display: 'flex', marginBottom: '12px', alignItems: 'center' }}>
                    <label style={{ fontWeight: 'bold', width: '120px', color: '#374151' }}>Created:</label>
                    <span style={{ color: '#6b7280' }}>{selectedTreeNode.metadata.created}</span>
                  </div>
                )}
                {selectedTreeNode.metadata?.count && (
                  <div className="detail-row" style={{ display: 'flex', marginBottom: '12px', alignItems: 'center' }}>
                    <label style={{ fontWeight: 'bold', width: '120px', color: '#374151' }}>Count:</label>
                    <span style={{ color: '#6b7280' }}>{selectedTreeNode.metadata.count}</span>
                  </div>
                )}
                {selectedTreeNode.metadata?.description && (
                  <div className="detail-row" style={{ display: 'flex', marginBottom: '12px', alignItems: 'center' }}>
                    <label style={{ fontWeight: 'bold', width: '120px', color: '#374151' }}>Description:</label>
                    <span style={{ color: '#6b7280' }}>{selectedTreeNode.metadata.description}</span>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="modal-actions">
            <button className="modal-action-btn update">Update</button>
            <button className="modal-action-btn approve">Approve</button>
            <button className="modal-action-btn inactivate">Inactivate</button>
            <button className="modal-action-btn delete">Delete</button>
            <button
              className="modal-action-btn inactivate"
              onClick={onClose}
            >
              Close
            </button>
          </div>
    </ModalPortal>
  );
};

export default DataTableDetailModal;